import { Observable, of, pipe, UnaryFunction } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { State } from './work.state';
import { EnvironmentVariables, StateReducer } from '@adreno-common';
import { Injectable } from '@angular/core';
import { ScriptWorkerService } from './script-worker.service';
import {
  PaymentGatewayService,
  RecordingService,
  SurveyService,
  TimezoneService,
} from '.';
import { ToastrService } from 'ngx-toastr';
import {
  callback,
  closeDisable,
  closeScript,
  displayScript,
  errorMessage,
  executeEntry,
  executeExit,
  getNewRecord,
  infoMessage,
  masterRecordCheck,
  noNavBack,
  pauseMonitor,
  scriptDisable,
  setContactUtcOffset,
  startSearch,
  webApi,
  webCapture,
  webExternal,
  webInternal,
  pcsData,
  pcsCancel,
  survey,
  paymentGateway,
  transferCall,
} from '../reducers';
import { WebPopService } from './web-pop.service';
import { CallbackService } from './callback.service';
import { RecordService } from './record.service';
@Injectable({
  providedIn: 'root',
})
export class WorkReducerService implements StateReducer<State> {
  constructor(
    private scriptWorker: ScriptWorkerService,
    private timeZone: TimezoneService,
    private toastr: ToastrService,
    private webPop: WebPopService,
    private recordingSvc: RecordingService,
    private callbackService: CallbackService,
    private recordSvc: RecordService,
    private surveySvc: SurveyService,
    private paymentGatewaySvc: PaymentGatewayService,
    private environment: EnvironmentVariables
  ) {}

  private processNav = (): UnaryFunction<
    Observable<[Partial<State>, State]>,
    Observable<[Partial<State>, State]>
  > =>
    pipe(
      this.resetToasts(),
      executeExit(this.scriptWorker, this.environment),
      this.processState(),
      executeEntry(this.scriptWorker, this.environment),
      this.processState(),
      displayScript(this.scriptWorker),
      switchMap((states) => {
        const [state, currentState] = states;
        if (state.system?.navToQ && state.system?.navToQ != '?') {
          //there are additional "auto nav" (entry script called navToQ)
          return of(states).pipe(this.processNav());
        }
        return of(states);
      })
    ) as UnaryFunction<
      Observable<[Partial<State>, State]>,
      Observable<[Partial<State>, State]>
    >;

  private processState = () =>
    pipe(
      getNewRecord(this.recordSvc, this.toastr),
      startSearch(this.recordSvc, this.toastr),
      callback(this.callbackService),
      pcsData(),
      pcsCancel(),
      closeDisable(),
      closeScript(this.webPop),
      pauseMonitor(this.recordingSvc),
      transferCall(),
      setContactUtcOffset(this.timeZone),
      infoMessage(this.toastr),
      errorMessage(this.toastr),
      noNavBack(),
      scriptDisable(),
      webApi(this.webPop),
      webCapture(this.webPop),
      webExternal(this.webPop),
      webInternal(this.webPop),
      survey(this.surveySvc),
      paymentGateway(this.paymentGatewaySvc),
      masterRecordCheck(this.toastr)
    ) as UnaryFunction<
      Observable<[Partial<State>, State]>,
      Observable<[Partial<State>, State]>
    >;

  private resetToasts(): UnaryFunction<
    Observable<[Partial<State>, State]>,
    Observable<[Partial<State>, State]>
  > {
    return map(([state, currentState]: [Partial<State>, State]) => {
      if (
        currentState.system?.activeToasts &&
        currentState.system.activeToasts.length > 0 &&
        state.system?.navToQ === '?'
      ) {
        currentState.system?.activeToasts.forEach((toastId) =>
          this.toastr.clear(toastId)
        );
        return [
          {
            ...state,
            system: {
              ...state.system,
              activeToasts: [],
            },
          },
          currentState,
        ] as [Partial<State>, State];
      }
      return [state, currentState] as [Partial<State>, State];
    });
  }

  reduce = () =>
    pipe(
      this.processNav(),
      map(([state, currentState]: [Partial<State>, State]) => state)
    ) as UnaryFunction<
      Observable<[Partial<State>, State]>,
      Observable<Partial<State>>
    >;
}
